import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { storage, CART_STORAGE_KEY } from '../lib/utils';
import { logger } from '../lib/logger';
import { useAuth } from './AuthContext';
import type {
  LocalCartItem,
  CartItemWithProduct,
  Product,
  ProductVariant,
} from '../types';

interface CartContextType {
  items: CartItemWithProduct[];
  itemCount: number;
  subtotal: number;
  isLoading: boolean;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (productId: string, variantId?: string | null, quantity?: number) => Promise<void>;
  removeItem: (productId: string, variantId?: string | null) => Promise<void>;
  updateQuantity: (productId: string, variantId: string | null, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  syncCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState<CartItemWithProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [localCart, setLocalCart] = useState<LocalCartItem[]>(() =>
    storage.get<LocalCartItem[]>(CART_STORAGE_KEY, [])
  );

  // Sauvegarder le panier local dans le localStorage
  useEffect(() => {
    if (!isAuthenticated) {
      storage.set(CART_STORAGE_KEY, localCart);
    }
  }, [localCart, isAuthenticated]);

  // Charger les détails des produits pour le panier local
  const loadProductDetails = useCallback(async (cartItems: LocalCartItem[]): Promise<CartItemWithProduct[]> => {
    if (cartItems.length === 0) return [];

    const productIds = [...new Set(cartItems.map((item) => item.productId))];
    const variantIds = cartItems
      .filter((item) => item.variantId)
      .map((item) => item.variantId) as string[];

    const [productsResult, variantsResult] = await Promise.all([
      supabase.from('products').select('*, images:product_images(*)').in('id', productIds),
      variantIds.length > 0
        ? supabase.from('product_variants').select('*').in('id', variantIds)
        : { data: [], error: null },
    ]);

    if (productsResult.error) {
      logger.error('Erreur chargement produits:', productsResult.error);
      return [];
    }

    const products = productsResult.data as Product[];
    const variants = (variantsResult.data || []) as ProductVariant[];

    return cartItems
      .map((item) => {
        const product = products.find((p) => p.id === item.productId);
        if (!product) return null;

        const variant = item.variantId
          ? variants.find((v) => v.id === item.variantId)
          : null;

        return {
          id: `${item.productId}-${item.variantId || 'default'}`,
          user_id: user?.id || '',
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          product,
          variant,
        } as CartItemWithProduct;
      })
      .filter((item): item is CartItemWithProduct => item !== null);
  }, [user?.id]);

  // Charger le panier depuis Supabase pour les utilisateurs connectés
  const loadCartFromSupabase = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          *,
          product:products(*, images:product_images(*)),
          variant:product_variants(*)
        `)
        .eq('user_id', user.id);

      if (error) {
        if (error.message.includes('AbortError')) {
          return;
        }
        logger.error('Erreur chargement panier:', error);
        return;
      }

      setItems(data as CartItemWithProduct[]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Charger le panier au changement d'état d'authentification
  useEffect(() => {
    const loadCart = async () => {
      if (isAuthenticated && user) {
        // Si l'utilisateur vient de se connecter et a un panier local, le synchroniser
        if (localCart.length > 0) {
          // Sync local cart to Supabase
          setIsLoading(true);
          try {
            for (const item of localCart) {
              // Check if item already exists in Supabase cart
              const { data: existing } = await supabase
                .from('cart_items')
                .select('id, quantity')
                .eq('user_id', user.id)
                .eq('product_id', item.productId)
                .eq('variant_id', item.variantId)
                .single();

              if (existing) {
                // Update quantity
                await supabase
                  .from('cart_items')
                  .update({ quantity: existing.quantity + item.quantity })
                  .eq('id', existing.id);
              } else {
                // Insert new item
                await supabase.from('cart_items').insert({
                  user_id: user.id,
                  product_id: item.productId,
                  variant_id: item.variantId,
                  quantity: item.quantity,
                });
              }
            }

            // Clear local cart after sync
            setLocalCart([]);
            storage.remove(CART_STORAGE_KEY);
          } catch (error) {
            logger.error('Erreur sync panier:', error);
          } finally {
            setIsLoading(false);
          }
        }
        await loadCartFromSupabase();
      } else {
        const cartWithProducts = await loadProductDetails(localCart);
        setItems(cartWithProducts);
      }
    };
    loadCart();
  }, [isAuthenticated, user, loadCartFromSupabase, loadProductDetails, localCart]);

  // Synchroniser le panier local avec Supabase lors de la connexion
  const syncCart = useCallback(async () => {
    if (!user || localCart.length === 0) return;

    setIsLoading(true);
    try {
      for (const item of localCart) {
        await supabase.from('cart_items').upsert(
          {
            user_id: user.id,
            product_id: item.productId,
            variant_id: item.variantId,
            quantity: item.quantity,
          },
          {
            onConflict: 'user_id,product_id,variant_id',
          }
        );
      }

      // Vider le panier local après synchronisation
      setLocalCart([]);
      storage.remove(CART_STORAGE_KEY);

      // Recharger le panier depuis Supabase
      await loadCartFromSupabase();
    } finally {
      setIsLoading(false);
    }
  }, [user, localCart, loadCartFromSupabase]);

  // Ajouter un article au panier
  const addItem = useCallback(
    async (productId: string, variantId: string | null = null, quantity: number = 1) => {
      if (isAuthenticated && user) {
        // Panier Supabase
        const existingItem = items.find(
          (item) => item.product_id === productId && item.variant_id === variantId
        );

        if (existingItem) {
          await supabase
            .from('cart_items')
            .update({ quantity: existingItem.quantity + quantity })
            .eq('id', existingItem.id);
        } else {
          await supabase.from('cart_items').insert({
            user_id: user.id,
            product_id: productId,
            variant_id: variantId,
            quantity,
          });
        }

        await loadCartFromSupabase();
      } else {
        // Panier local
        setLocalCart((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.productId === productId && item.variantId === variantId
          );

          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex].quantity += quantity;
            return updated;
          }

          return [...prev, { productId, variantId, quantity }];
        });
      }
    },
    [isAuthenticated, user, items, loadCartFromSupabase]
  );

  // Supprimer un article du panier
  const removeItem = useCallback(
    async (productId: string, variantId: string | null = null) => {
      if (isAuthenticated && user) {
        const item = items.find(
          (i) => i.product_id === productId && i.variant_id === variantId
        );
        if (item) {
          await supabase.from('cart_items').delete().eq('id', item.id);
          await loadCartFromSupabase();
        }
      } else {
        setLocalCart((prev) =>
          prev.filter(
            (item) => !(item.productId === productId && item.variantId === variantId)
          )
        );
      }
    },
    [isAuthenticated, user, items, loadCartFromSupabase]
  );

  // Mettre à jour la quantité
  const updateQuantity = useCallback(
    async (productId: string, variantId: string | null, quantity: number) => {
      if (quantity <= 0) {
        await removeItem(productId, variantId);
        return;
      }

      if (isAuthenticated && user) {
        const item = items.find(
          (i) => i.product_id === productId && i.variant_id === variantId
        );
        if (item) {
          await supabase
            .from('cart_items')
            .update({ quantity })
            .eq('id', item.id);
          await loadCartFromSupabase();
        }
      } else {
        setLocalCart((prev) =>
          prev.map((item) =>
            item.productId === productId && item.variantId === variantId
              ? { ...item, quantity }
              : item
          )
        );
      }
    },
    [isAuthenticated, user, items, removeItem, loadCartFromSupabase]
  );

  // Vider le panier
  const clearCart = useCallback(async () => {
    if (isAuthenticated && user) {
      await supabase.from('cart_items').delete().eq('user_id', user.id);
      await loadCartFromSupabase();
    } else {
      setLocalCart([]);
    }
  }, [isAuthenticated, user, loadCartFromSupabase]);

  // Calculer le nombre total d'articles
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calculer le sous-total
  const subtotal = items.reduce((sum, item) => {
    const basePrice = item.product.price;
    const adjustment = item.variant?.price_adjustment || 0;
    return sum + (basePrice + adjustment) * item.quantity;
  }, 0);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen((prev) => !prev);

  const value: CartContextType = {
    items,
    itemCount,
    subtotal,
    isLoading,
    isOpen,
    openCart,
    closeCart,
    toggleCart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    syncCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
}
